import * as React from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import { ISalesPointDefaultFormValues, ISalesPointFormValues, salesPointFormSchema } from '../../../../@types/model/masterData/salesPoint/salesPointFormValues';
import { useAppSelector } from '../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { IOptionType } from '../../../../@types/helper';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';

interface ISalesPointFormProps {
    isLoading : boolean;
    initialValues : ISalesPointFormValues | ISalesPointDefaultFormValues;
    onSubmit : (values : ISalesPointFormValues) => void;
    onClose : () => void;
}

const SalesPointForm = (props : ISalesPointFormProps) : React.ReactElement => {
    const schema = salesPointFormSchema;
    const { initialValues, onSubmit, onClose, isLoading } = props;

    const { handleSubmit, control, formState: { isValid, isDirty } } = useForm<ISalesPointFormValues>({
        defaultValues: initialValues,
        resolver: zodResolver(schema),
        mode: 'onChange',
    });

    const farms = useAppSelector(state => state.masterData.farms);

    const farmOptions : Array<IOptionType> = useMemo(() => {
        return farms?.filter(x => x.isActive).map(x => ({ label: `(${x.code}) ${x.name}`, value: x.id })) ?? [];
    }, [farms]);

    return (
        <div>
            <CustomReactHookFormDevTools control={control} />
            <form onSubmit={handleSubmit(onSubmit)} className={'p20 mnw400'}>
                <div className={'fdc aic'}>
                    <ControlledTextField
                        className={'mb10 w100'}
                        name={'name'}
                        label={'Name'}
                        control={control}
                        defaultValue={initialValues.name} />
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10 w100'}
                        name={'farms'}
                        label={'Farms'}
                        control={control}
                        options={farmOptions}
                        defaultValues={initialValues.farms}
                        isMulti />
                    <div className={'fdr jcfs mb10 w100'}>
                        <ControlledLabelledCheckBox
                            className={'p0'}
                            name={'isActive'}
                            label={'Is Active'}
                            control={control}
                            defaultValue={initialValues.isActive} />
                    </div>
                    <div className={'fdr ml10 ais jcfe pt10 pb10 w100'}>
                        <Button className={'cpd mr20'} type={'button'} onClick={onClose}>
                            CANCEL
                        </Button>
                        <PillButton
                            disabled={!isDirty || !isValid || isLoading}
                            className={'ml15 pl20 pr20 h35'}
                            text={'Save'}
                            type={'submit'}
                            color={'secondary'} />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default SalesPointForm;
